import * as maplibregl from "maplibre-gl";
import {
  HOME_BOUNDS,
  WAYPOINT_COLORS,
  WAYPOINT_FALLBACK,
  bindBasemapSwitch,
  boundsOf,
  buildStyle,
  el,
  escapeHtml,
  fmtDrive,
  fmtMiles,
  json,
} from "./shared";

// The 444 Club page: the whole main stem as one chain of trips, and a menu
// of those trips to sort and filter by what makes a weekend work. Everything
// here is measured at build time and read from chains.json.

type Kind = "done" | "chill" | "ambitious" | "long";

type Link = {
  n: number;
  id: string;
  name: string;
  status: string;
  kind: Kind;
  year: string | null;
  miles: number;
  fromMile: number;
  toMile: number;
  states: string[];
  putIn: { name: string; riverMile: number };
  takeOut: { name: string; riverMile: number };
  nights: { n: number; name: string; offset: number; notes: string; booking: string | null; fee: boolean | null }[];
  nightCount: number;
  days: number[];
  driveMinutes: number | null;
  returnMinutes: number | null;
  shuttleMinutes: number | null;
  water: { risk: "low" | "moderate" | "high"; gauge?: string; note?: string } | null;
  portages: { name: string; notes: string }[];
  hazards: { name: string; notes: string }[];
  towns: number;
  longestNoRoad: number;
  paddleHours: number;
  resupplyStops: number;
  longestNoResupply: number;
  difficulty: { level: "easy" | "medium" | "hard"; score: number; reasons: string[] };
  pitch: string;
  notes: string;
};

type Problem = {
  kind: "gap" | "overlap";
  after: number;
  fromMile: number;
  toMile: number;
  miles: number;
  geometry?: GeoJSON.LineString;
};

type Chain = {
  id: string;
  name: string;
  blurb: string;
  topMile: number;
  paddleMph: number;
  portageHours: number;
  links: Link[];
  problems: Problem[];
  states: { state: string; fromMile: number; toMile: number }[];
  totals: { links: number; doneMiles: number; plannedMiles: number; nights: number };
};

type Trip = { id: string; geometry: GeoJSON.LineString };

// Four kinds, four colours: the orange that means "paddled" everywhere on the
// site, and three slots from the validated trip palette (all pairs, on the
// dark panel — see build-trips.mjs). The kind is always written beside the
// colour, so it is never the only cue.
const KINDS: Record<Kind, { label: string; color: string; blurb: string }> = {
  done: { label: "Done", color: "#e2701e", blurb: "paddled" },
  chill: { label: "Chill", color: "#4897cb", blurb: "2 nights, easy days" },
  ambitious: { label: "Ambitious", color: "#cc3a92", blurb: "2 nights, big days" },
  long: { label: "3-night", color: "#9257e0", blurb: "3 nights" },
};
const GAP_COLOR = "#8ca9b2";

const WATER: Record<string, { label: string; rank: number }> = {
  low: { label: "Reliable", rank: 0 },
  moderate: { label: "Can be low", rank: 1 },
  high: { label: "Spring only", rank: 2 },
};

const DIFFICULTY: Record<string, { label: string; rank: number }> = {
  easy: { label: "Easy", rank: 0 },
  medium: { label: "Medium", rank: 1 },
  hard: { label: "Hard", rank: 2 },
};

const dayNames = (count: number) => ["Wed", "Thu", "Fri", "Sat", "Sun"].slice(-count);

const bookingLabel = (b: string | null) =>
  b === "permit"
    ? "permit"
    : b === "reserve"
      ? "reserve"
      : b === "first-come"
        ? "first come"
        : b === "call"
          ? "call ahead"
          : b === "ask"
            ? "ask first"
            : "";

type Filters = {
  kinds: Set<Kind>;
  drive: number | null;
  water: string;
  camps: string;
  portage: number | null;
  state: string;
  remote: number | null;
  difficulty: string;
};

async function main() {
  const [chains, trips, waypoints] = await Promise.all([
    json<Chain[]>("data/chains.json"),
    json<Trip[]>("data/trips.json"),
    json<GeoJSON.FeatureCollection>("data/waypoints.geojson"),
  ]);
  const chain = chains.find((c) => c.id === "444")!;
  const geometryOf = new Map(trips.map((t) => [t.id, t.geometry]));
  const links = chain.links;

  const filters: Filters = {
    kinds: new Set<Kind>(["done", "chill", "ambitious", "long"]),
    drive: null,
    water: "",
    camps: "",
    portage: null,
    state: "",
    remote: null,
    difficulty: "",
  };
  let sortKey = "n";
  let sortDir = 1;
  let current: Link | null = null;

  renderHead(chain);

  // --- map ---

  const map = new maplibregl.Map({
    container: "map",
    style: buildStyle(),
    bounds: HOME_BOUNDS,
    fitBoundsOptions: { padding: 40 },
    attributionControl: { compact: true },
  });
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
  map.addControl(new maplibregl.ScaleControl({ unit: "imperial" }), "bottom-right");
  bindBasemapSwitch(map);
  const popup = new maplibregl.Popup({ closeButton: false, offset: 12, maxWidth: "250px" });

  const linkFeatures: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: links.map((l) => ({
      type: "Feature",
      id: l.n,
      properties: { id: l.id, n: l.n, color: KINDS[l.kind].color, done: l.kind === "done" },
      geometry: geometryOf.get(l.id)!,
    })),
  };
  const gapFeatures: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: chain.problems
      .filter((p) => p.geometry)
      .map((p) => ({ type: "Feature", properties: {}, geometry: p.geometry! })),
  };
  const linkIds = new Set(links.filter((l) => l.kind !== "done").map((l) => l.id));
  const stopFeatures: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: waypoints.features.filter((f) => linkIds.has((f.properties as { tripId: string }).tripId)),
  };

  const addLayers = () => {
    map.addSource("gaps", { type: "geojson", data: gapFeatures });
    map.addSource("links", { type: "geojson", data: linkFeatures });
    map.addSource("stops", { type: "geojson", data: stopFeatures });

    map.addLayer({
      id: "gaps-line",
      type: "line",
      source: "gaps",
      paint: {
        "line-color": GAP_COLOR,
        "line-dasharray": [1, 2],
        "line-width": ["interpolate", ["linear"], ["zoom"], 6, 1.6, 12, 3],
      },
    });
    map.addLayer({
      id: "links-casing",
      type: "line",
      source: "links",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#0e1f26",
        "line-opacity": ["case", ["boolean", ["feature-state", "hidden"], false], 0.15, 0.55],
        "line-width": ["interpolate", ["linear"], ["zoom"], 6, 6, 10, 9, 14, 13],
      },
    });
    const selected = [
      "boolean",
      ["feature-state", "selected"],
      false,
    ] as unknown as maplibregl.ExpressionSpecification;
    map.addLayer({
      id: "links-line",
      type: "line",
      source: "links",
      layout: { "line-cap": "butt", "line-join": "round" },
      paint: {
        "line-color": ["case", selected, "#e4ecea", ["get", "color"]],
        "line-opacity": ["case", ["boolean", ["feature-state", "hidden"], false], 0.2, 1],
        // Done water is solid, planned water dashed, as on the rest of the site.
        "line-dasharray": ["case", ["get", "done"], ["literal", [1, 0]], ["literal", [3, 1.5]]],
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          6,
          ["case", selected, 4, 2.6],
          10,
          ["case", selected, 6, 4],
          14,
          ["case", selected, 9, 6],
        ],
      },
    });
    map.addLayer({
      id: "stop-dots",
      type: "circle",
      source: "stops",
      filter: ["==", ["get", "tripId"], ""],
      paint: {
        "circle-color": [
          "match",
          ["get", "type"],
          ...Object.entries(WAYPOINT_COLORS).flat(),
          WAYPOINT_FALLBACK,
        ] as unknown as maplibregl.ExpressionSpecification,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 7, 4, 12, 7],
        "circle-stroke-color": "#0e1f26",
        "circle-stroke-width": 1.5,
      },
    });

    map.on("click", "links-line", (e) => {
      const n = (e.features?.[0]?.properties as { n?: number })?.n;
      const link = links.find((l) => l.n === n);
      if (link) select(link, true);
    });
    map.on("mouseenter", "links-line", () => (map.getCanvas().style.cursor = "pointer"));
    map.on("mouseleave", "links-line", () => (map.getCanvas().style.cursor = ""));
    map.on("mouseenter", "stop-dots", (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as { name: string; notes: string; riverMile: number; offRiver: boolean };
      popup
        .setLngLat((f.geometry as GeoJSON.Point).coordinates as [number, number])
        .setHTML(
          `<span class="popup-mile">${escapeHtml(p.offRiver ? "Off river" : `Mile ${p.riverMile}`)}</span>` +
            `<span class="popup-title">${escapeHtml(p.name)}</span>` +
            (p.notes ? `<div class="popup-body">${escapeHtml(p.notes)}</div>` : ""),
        )
        .addTo(map);
    });
    map.on("mouseleave", "stop-dots", () => popup.remove());
    applyToMap();
  };
  if (map.isStyleLoaded()) addLayers();
  else map.once("style.load", addLayers);

  // Numbered badges at each planned link's middle, so the map and the menu
  // share names. HTML markers: the map has no glyph server.
  const badges = new Map<number, HTMLButtonElement>();
  for (const l of links) {
    const coords = geometryOf.get(l.id)!.coordinates;
    const badge = document.createElement("button");
    badge.type = "button";
    badge.className = "option-badge";
    badge.textContent = String(l.n);
    badge.style.setProperty("--option-color", KINDS[l.kind].color);
    badge.setAttribute("aria-label", `Link ${l.n}: ${l.name}`);
    badge.addEventListener("click", (e) => {
      e.stopPropagation();
      select(l, true);
    });
    badges.set(l.n, badge);
    new maplibregl.Marker({ element: badge })
      .setLngLat(coords[Math.floor(coords.length / 2)] as [number, number])
      .addTo(map);
  }

  // --- filtering and sorting ---

  const passes = (l: Link) => {
    if (!filters.kinds.has(l.kind)) return false;
    if (filters.drive !== null && (l.driveMinutes ?? Infinity) > filters.drive) return false;
    if (filters.water === "low" && l.water?.risk !== "low") return false;
    if (filters.water === "moderate" && l.water?.risk === "high") return false;
    if (filters.camps === "scouted" && l.kind !== "done" && l.nights.length < l.nightCount) return false;
    if (filters.camps === "nobook" && l.nights.some((n) => n.booking && n.booking !== "first-come")) return false;
    if (filters.portage !== null && l.portages.length > filters.portage) return false;
    if (filters.state && !l.states.includes(filters.state)) return false;
    if (filters.remote !== null && l.longestNoRoad > filters.remote) return false;
    const d = DIFFICULTY[l.difficulty.level].rank;
    if (filters.difficulty === "easy" && d > 0) return false;
    if (filters.difficulty === "medium" && d > 1) return false;
    if (filters.difficulty === "hard" && d < 2) return false;
    return true;
  };

  const sortValue = (l: Link): number | string => {
    switch (sortKey) {
      case "name":
        return l.name;
      case "miles":
        return l.miles;
      case "nights":
        return l.nightCount;
      case "drive":
        return l.driveMinutes ?? Infinity;
      case "water":
        return l.water ? WATER[l.water.risk].rank : -1;
      case "camps":
        return l.nights.filter((n) => n.booking && n.booking !== "first-come").length;
      case "portages":
        return l.portages.length;
      case "remote":
        return l.longestNoRoad;
      case "difficulty":
        return l.difficulty.score;
      default:
        return l.n;
    }
  };

  function refresh() {
    const shown = links.filter(passes);
    const ordered = [...shown].sort((a, b) => {
      const x = sortValue(a);
      const y = sortValue(b);
      return (x < y ? -1 : x > y ? 1 : 0) * sortDir || a.n - b.n;
    });
    renderMenu(ordered, current, select);
    for (const seg of document.querySelectorAll<HTMLElement>(".chain-seg[data-n]")) {
      const link = links.find((l) => l.n === Number(seg.dataset.n))!;
      seg.classList.toggle("is-dim", !passes(link));
    }
    for (const th of document.querySelectorAll<HTMLButtonElement>("#menu-table th button")) {
      const on = th.dataset.sort === sortKey;
      th.parentElement!.setAttribute("aria-sort", on ? (sortDir > 0 ? "ascending" : "descending") : "none");
    }
    el("filter-count").textContent =
      shown.length === links.length
        ? `All ${links.length} links`
        : `${shown.length} of ${links.length} links · ${fmtMiles(shown.reduce((a, l) => a + l.miles, 0))} mi`;
    applyToMap();
  }

  function applyToMap() {
    for (const l of links) {
      const hidden = !passes(l);
      badges.get(l.n)!.style.display = hidden ? "none" : "";
      if (map.getSource("links")) map.setFeatureState({ source: "links", id: l.n }, { hidden });
    }
  }

  // Kind chips double as the legend: colour, name and count together.
  el("kind-chips").innerHTML = (Object.keys(KINDS) as Kind[])
    .map((k) => {
      const count = links.filter((l) => l.kind === k).length;
      return `<button type="button" class="chip" data-kind="${k}" aria-pressed="true" ${count ? "" : "disabled"}>
        <i style="background:${KINDS[k].color}"></i>${KINDS[k].label} <span>${count}</span>
      </button>`;
    })
    .join("");
  for (const chip of document.querySelectorAll<HTMLButtonElement>("#kind-chips .chip")) {
    chip.addEventListener("click", () => {
      const k = chip.dataset.kind as Kind;
      if (filters.kinds.has(k)) filters.kinds.delete(k);
      else filters.kinds.add(k);
      chip.setAttribute("aria-pressed", String(filters.kinds.has(k)));
      refresh();
    });
  }
  const numberOrNull = (v: string) => (v === "" ? null : Number(v));
  const bind = (id: string, apply: (v: string) => void) =>
    el<HTMLSelectElement>(id).addEventListener("change", (e) => {
      apply((e.target as HTMLSelectElement).value);
      refresh();
    });
  bind("f-drive", (v) => (filters.drive = numberOrNull(v)));
  bind("f-water", (v) => (filters.water = v));
  bind("f-camps", (v) => (filters.camps = v));
  bind("f-portage", (v) => (filters.portage = numberOrNull(v)));
  bind("f-state", (v) => (filters.state = v));
  bind("f-remote", (v) => (filters.remote = numberOrNull(v)));
  bind("f-difficulty", (v) => (filters.difficulty = v));

  for (const th of document.querySelectorAll<HTMLButtonElement>("#menu-table th button")) {
    th.addEventListener("click", () => {
      if (sortKey === th.dataset.sort) sortDir = -sortDir;
      else {
        sortKey = th.dataset.sort!;
        sortDir = 1;
      }
      refresh();
    });
  }

  // --- selection ---

  function select(link: Link, scroll: boolean) {
    current = current?.n === link.n && !scroll ? null : link;
    if (map.getSource("links")) {
      for (const l of links) {
        map.setFeatureState({ source: "links", id: l.n }, { selected: l.n === current?.n });
      }
      map.setFilter("stop-dots", ["==", ["get", "tripId"], current?.id ?? ""]);
    }
    for (const [n, b] of badges) b.classList.toggle("is-selected", n === current?.n);
    for (const seg of document.querySelectorAll<HTMLElement>(".chain-seg[data-n]")) {
      seg.classList.toggle("is-selected", Number(seg.dataset.n) === current?.n);
    }
    if (current) {
      map.fitBounds(boundsOf(geometryOf.get(current.id)!.coordinates), { padding: 70, maxZoom: 12, duration: 900 });
      history.replaceState(null, "", `#${current.n}`);
    } else {
      history.replaceState(null, "", location.pathname);
    }
    refresh();
    if (current && scroll) {
      document.querySelector(`tr[data-n="${current.n}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  for (const seg of document.querySelectorAll<HTMLButtonElement>(".chain-seg[data-n]")) {
    seg.addEventListener("click", () => select(links.find((l) => l.n === Number(seg.dataset.n))!, true));
  }

  el("reset-view").addEventListener("click", () => {
    if (current) select(current, false);
    map.fitBounds(HOME_BOUNDS, { padding: 40, duration: 900 });
  });

  refresh();
  const fromHash = links.find((l) => `#${l.n}` === location.hash);
  if (fromHash) select(fromHash, true);
  window.addEventListener("hashchange", () => {
    const l = links.find((x) => `#${x.n}` === location.hash);
    if (l && l.n !== current?.n) select(l, true);
  });
}

function renderHead(chain: Chain) {
  const total = chain.topMile;
  const t = chain.totals;
  const gapMiles = chain.problems.filter((p) => p.kind === "gap").reduce((a, p) => a + p.miles, 0);
  el("chain-title").textContent = chain.name;
  el("chain-figures").innerHTML =
    `<b>${fmtMiles(total)} mi</b> in ${t.links} links · <b>${fmtMiles(t.doneMiles)}</b> done · ` +
    `<b>${fmtMiles(t.plannedMiles)}</b> planned over ${t.nights} nights` +
    (gapMiles > 0.5 ? ` · <b>${fmtMiles(gapMiles)}</b> not yet planned` : "");

  // The strip reads source to mouth, left to right: each link at its true
  // length, gaps hatched, state lines marked above it.
  const pieces: string[] = [];
  let cursor = total;
  const gapAt = (from: number, to: number) =>
    `<span class="chain-seg is-gap" role="listitem" style="flex-grow:${from - to}" title="Not yet planned: RM ${from.toFixed(0)}–${to.toFixed(0)}"></span>`;
  for (const l of chain.links) {
    if (cursor - l.fromMile > 0.5) pieces.push(gapAt(cursor, l.fromMile));
    pieces.push(
      `<button type="button" class="chain-seg" role="listitem" data-n="${l.n}" style="flex-grow:${l.miles};--seg:${KINDS[l.kind].color}" title="${escapeHtml(
        `${l.n}. ${l.name} · ${fmtMiles(l.miles)} mi · ${KINDS[l.kind].label}`,
      )}"><span>${l.n}</span></button>`,
    );
    cursor = l.toMile;
  }
  if (cursor > 0.5) pieces.push(gapAt(cursor, 0));
  const states = chain.states
    .map(
      (s) =>
        `<span class="chain-state" style="flex-grow:${Math.max(s.fromMile - s.toMile, 0.1)}">${s.fromMile - s.toMile > 12 ? s.state : ""}</span>`,
    )
    .join("");
  el("chain-strip").innerHTML = `<div class="chain-states" aria-hidden="true">${states}</div><div class="chain-segs">${pieces.join("")}</div>`;

  el("chain-key").innerHTML =
    (Object.keys(KINDS) as Kind[])
      .map((k) => `<span><i style="background:${KINDS[k].color}"></i>${KINDS[k].label} · ${KINDS[k].blurb}</span>`)
      .join("") + (gapMiles > 0.5 ? `<span><i class="gap"></i>Not yet planned</span>` : "");

  el("chain-problems").innerHTML = chain.problems
    .filter((p) => p.kind === "overlap")
    .map(
      (p) =>
        `<p class="option-flag">Links ${p.after} and ${p.after + 1} overlap by ${fmtMiles(p.miles)} mi (RM ${p.toMile.toFixed(0)}–${p.fromMile.toFixed(0)}).</p>`,
    )
    .join("");

  el("menu-note").textContent =
    `Paddling time assumes ${chain.paddleMph} mph plus ${Math.round(chain.portageHours * 60)} minutes a portage. ` +
    `Remote is the longest stretch between road access points. Drive is Lancaster to the put-in. ` +
    `Difficulty weighs the longest stretch without food, water or ice (towns, food stops and paid ` +
    `campgrounds count), then day length, portages, water and camps without permission.`;

  el("legend").innerHTML =
    (Object.keys(KINDS) as Kind[])
      .map((k) => `<span class="key"><i class="swatch" style="background:${KINDS[k].color}"></i> ${KINDS[k].label}</span>`)
      .join("") +
    (gapMiles > 0.5 ? `<span class="key"><i class="swatch gapline"></i> Not yet planned</span>` : "");
}

function renderMenu(rows: Link[], current: Link | null, onSelect: (l: Link, scroll: boolean) => void) {
  const body = el("menu-body");
  if (!rows.length) {
    body.innerHTML = `<tr><td colspan="10" class="menu-empty">Nothing matches — loosen a filter.</td></tr>`;
    return;
  }
  body.innerHTML = rows
    .map((l) => {
      const k = KINDS[l.kind];
      const water = l.water ? WATER[l.water.risk].label : l.kind === "done" ? "—" : "?";
      // A night with no legal camp yet ("ask") is a different problem from one
      // that just needs booking, so it is counted apart.
      const asks = l.nights.filter((n) => n.booking === "ask").length;
      const booked = l.nights.filter((n) => n.booking && n.booking !== "first-come" && n.booking !== "ask").length;
      const camps =
        l.kind === "done"
          ? "—"
          : [
              `${l.nights.length}/${l.nightCount}`,
              booked ? `${booked} to book` : "",
              asks ? `${asks} ask first` : "",
            ]
              .filter(Boolean)
              .join(" · ");
      const row = `<tr data-n="${l.n}" class="${current?.n === l.n ? "is-selected" : ""}" tabindex="0" aria-expanded="${current?.n === l.n}">
        <td><span class="option-n" style="--option-color:${k.color}">${l.n}</span></td>
        <td class="menu-name"><span>${escapeHtml(l.name)}</span><span class="menu-sub"><i style="background:${k.color}"></i>${k.label}${l.year ? ` · ${l.year}` : ""} · ${escapeHtml(l.states.join("→"))}</span></td>
        <td class="num">${fmtMiles(l.miles)}</td>
        <td class="num">${l.nightCount}</td>
        <td><span class="difficulty ${l.difficulty.level}">${DIFFICULTY[l.difficulty.level].label}</span></td>
        <td class="num">${l.driveMinutes ? fmtDrive(l.driveMinutes) : "—"}</td>
        <td>${escapeHtml(water)}</td>
        <td>${escapeHtml(camps)}</td>
        <td class="num">${l.portages.length || "—"}</td>
        <td class="num">${fmtMiles(l.longestNoRoad)}</td>
      </tr>`;
      return current?.n === l.n ? row + detailRow(l) : row;
    })
    .join("");

  for (const tr of body.querySelectorAll<HTMLTableRowElement>("tr[data-n]")) {
    const pick = () => onSelect(rows.find((l) => l.n === Number(tr.dataset.n))!, false);
    tr.addEventListener("click", pick);
    tr.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        pick();
      }
    });
  }
}

function detailRow(l: Link): string {
  const names = dayNames(l.days.length);
  const strip = l.days.length
    ? `<div class="strip-wrap" style="width:100%"><div class="strip">${l.days
        .map(
          (m, i) =>
            `<span class="strip-day" style="flex-grow:${Math.max(m, 0.01)}"><span class="strip-label">${names[i]} <b>${fmtMiles(m)}</b></span></span>`,
        )
        .join("")}${l.nights
        .map((n) => `<i class="strip-camp" style="left:${((n.offset / l.miles) * 100).toFixed(2)}%"></i>`)
        .join("")}</div></div>`
    : "";
  const nights = l.nights
    .map(
      (n) => `<li><span class="night-n">Night ${n.n}</span><span class="night-where">${escapeHtml(n.name)} · ${fmtMiles(n.offset)} mi in${
        n.notes ? `<span class="night-note">${escapeHtml(n.notes)}</span>` : ""
      }</span>${n.booking ? `<span class="verdict ${n.booking === "first-come" ? "fits" : "stretch"}">${escapeHtml(bookingLabel(n.booking))}</span>` : ""}</li>`,
    )
    .join("");
  const drive = [
    l.driveMinutes ? `${fmtDrive(l.driveMinutes)} out` : "",
    l.shuttleMinutes ? `${fmtDrive(l.shuttleMinutes)} shuttle` : "",
    l.returnMinutes ? `${fmtDrive(l.returnMinutes)} home` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const list = (title: string, items: { name: string; notes: string }[]) =>
    items.length
      ? `<h4 class="option-sub">${title}</h4><ul class="detail-list">${items
          .map((x) => `<li><b>${escapeHtml(x.name)}</b>${x.notes ? ` — ${escapeHtml(x.notes)}` : ""}</li>`)
          .join("")}</ul>`
      : "";

  return `<tr class="menu-detail"><td colspan="10">
    ${l.pitch ? `<p class="option-pitch">${escapeHtml(l.pitch)}</p>` : ""}
    <p class="option-why"><span class="difficulty ${l.difficulty.level}">${DIFFICULTY[l.difficulty.level].label}</span> ${
      l.difficulty.reasons.length ? escapeHtml(l.difficulty.reasons.join(" · ")) : "Short gaps between resupply, moderate days, nothing to portage."
    }</p>
    <p class="option-meta">RM ${l.fromMile.toFixed(0)}–${l.toMile.toFixed(0)} · ${escapeHtml(l.putIn.name)} → ${escapeHtml(l.takeOut.name)}</p>
    ${strip}
    ${nights ? `<ul class="nights">${nights}</ul>` : ""}
    <dl class="detail-facts">
      ${drive ? `<div><dt>Driving</dt><dd>${drive}</dd></div>` : ""}
      <div><dt>On the water</dt><dd>about ${l.paddleHours} hours</dd></div>
      <div><dt>Resupply stops</dt><dd>${l.resupplyStops} · longest gap ${fmtMiles(l.longestNoResupply)} mi</dd></div>
      <div><dt>Longest without a road</dt><dd>${fmtMiles(l.longestNoRoad)} mi</dd></div>
    </dl>
    ${
      l.water
        ? `<h4 class="option-sub">Water · ${escapeHtml(WATER[l.water.risk].label)}</h4><p class="option-why">${escapeHtml(
            [l.water.note, l.water.gauge ? `Gauge: ${l.water.gauge}.` : ""].filter(Boolean).join(" "),
          )}</p>`
        : ""
    }
    ${list("Dams and portages", l.portages)}
    ${list("Hazards", l.hazards)}
    ${l.notes ? `<p class="option-notes">${escapeHtml(l.notes)}</p>` : ""}
  </td></tr>`;
}

main().catch((err) => {
  console.error(err);
  el("menu-body").innerHTML = `<tr><td colspan="10">Could not load the chain. Run <code>npm run build</code>.</td></tr>`;
});
